/**
 * Campaign Goals Page (Super Admin)
 *
 * Tags each ad campaign with the goal it was run for, so CAC can be computed
 * against acquisition spend rather than all spend.
 *
 * Ordered by lifetime spend, biggest first, and that ordering is the point:
 * spend is heavily concentrated, so tagging the top handful of campaigns gets
 * coverage over the line while the long tail barely moves it. An alphabetical
 * list would hide that and make this look like 44 equally important rows.
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { adminAPI } from '../services/api'
import { channelColor, channelLabel } from '../lib/channels'
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle, Button, Badge,
} from '../components/ui'
import { RefreshCw, Loader2, AlertCircle, Sparkles, Check, X } from 'lucide-react'

/** CAC is only meaningful once most spend carries a goal. Mirrors
 *  MIN_COVERAGE_FOR_CAC in backend/src/services/campaignGoals.js. */
const CAC_COVERAGE_THRESHOLD = 0.8

const GOAL_STYLES = {
  acquisition: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  expansion: 'bg-blue-100 text-blue-800 border-blue-200',
  retention: 'bg-amber-100 text-amber-800 border-amber-200',
  brand: 'bg-purple-100 text-purple-800 border-purple-200',
  unknown: 'bg-gray-100 text-gray-600 border-gray-200',
}

function formatCurrency(dollars) {
  if (dollars === null || dollars === undefined || isNaN(dollars)) return '—'
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(dollars)
}

function formatPercent(share) {
  if (share === null || share === undefined || isNaN(share)) return '—'
  return `${(share * 100).toFixed(1)}%`
}

export default function CampaignGoalsPage() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(null)
  const [filter, setFilter] = useState('untagged')

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await adminAPI.getCampaignGoals()
      if (response.data.success) setData(response.data.data)
    } catch (err) {
      console.error('[CAMPAIGN-GOALS] Error:', err)
      setError(err.response?.data?.error?.message || err.message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchData() }, [fetchData])

  const setGoal = useCallback(async (campaign, goal) => {
    const key = `${campaign.channel}|${campaign.campaign_name}`
    try {
      setSaving(key)
      if (goal === '') {
        await adminAPI.deleteCampaignGoal({
          channel: campaign.channel,
          campaign_name: campaign.campaign_name,
        })
      } else {
        await adminAPI.saveCampaignGoal({
          channel: campaign.channel,
          campaign_name: campaign.campaign_name,
          brand: campaign.brand,
          goal,
        })
      }
      await fetchData()
    } catch (err) {
      console.error('[CAMPAIGN-GOALS] Save error:', err)
      setError(err.response?.data?.error?.message || err.message)
    } finally {
      setSaving(null)
    }
  }, [fetchData])

  const campaigns = data?.campaigns || []
  const coverage = data?.coverage
  const goals = data?.goals || []

  const filtered = useMemo(() => {
    if (filter === 'untagged') return campaigns.filter(c => !c.goal && !c.unnameable)
    if (filter === 'tagged') return campaigns.filter(c => c.goal)
    return campaigns
  }, [campaigns, filter])

  // How much untagged spend sits above the CAC threshold — i.e. what is still
  // needed, not just what is missing. Walking down the untagged rows by spend
  // tells you how many more you actually have to do.
  const remainingToThreshold = useMemo(() => {
    if (!coverage) return null
    const need = coverage.total_spend * CAC_COVERAGE_THRESHOLD - coverage.tagged_spend
    if (need <= 0) return { met: true, spend: 0, count: 0 }
    let acc = 0
    let count = 0
    for (const c of campaigns.filter(x => !x.goal && !x.unnameable)) {
      if (acc >= need) break
      acc += c.spend
      count++
    }
    return { met: false, spend: need, count }
  }, [campaigns, coverage])

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Campaign Goals</h1>
          <p className="text-sm text-gray-500 mt-1">
            Tag what each campaign was run for, so CAC and LTV:CAC can be measured against
            acquisition spend instead of total spend.
          </p>
        </div>
        <Button variant="outline" onClick={fetchData} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-md text-red-800">
          <AlertCircle className="h-5 w-5 shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {coverage && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Coverage</CardTitle>
            <CardDescription>
              {coverage.campaigns_tagged} of {coverage.campaigns_total} campaigns tagged,
              covering {formatCurrency(coverage.tagged_spend)} of {formatCurrency(coverage.total_spend)} lifetime spend.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="relative h-3 w-full rounded-full bg-gray-100 overflow-hidden">
              <div
                className="absolute inset-y-0 left-0 bg-emerald-500"
                style={{ width: `${Math.min(100, coverage.tagged_share * 100)}%` }}
              />
              {/* Spend that no mapping can reach, parked at the right-hand end so
                  the gap between the bar and the ceiling is visible. */}
              <div
                className="absolute inset-y-0 right-0 bg-gray-300"
                style={{ width: `${Math.min(100, (1 - coverage.max_achievable_share) * 100)}%` }}
                title="No campaign name in the spend data — cannot be tagged here"
              />
              <div
                className="absolute inset-y-0 w-0.5 bg-gray-900"
                style={{ left: `${CAC_COVERAGE_THRESHOLD * 100}%` }}
                title={`${CAC_COVERAGE_THRESHOLD * 100}% needed for CAC`}
              />
            </div>

            <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
              <span className="text-gray-900 font-medium">
                {formatPercent(coverage.tagged_share)} tagged
              </span>
              <span className="text-gray-500">
                {formatPercent(CAC_COVERAGE_THRESHOLD)} needed for CAC
              </span>
              <span className="text-gray-500">
                Ceiling {formatPercent(coverage.max_achievable_share)}
              </span>
            </div>

            {remainingToThreshold && !remainingToThreshold.met && (
              <p className="text-sm text-gray-600">
                Tagging the next <strong>{remainingToThreshold.count}</strong> untagged
                {remainingToThreshold.count === 1 ? ' campaign' : ' campaigns'} by spend
                reaches the {formatPercent(CAC_COVERAGE_THRESHOLD)} threshold.
              </p>
            )}

            {coverage.unnameable_spend > 0 && (
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
                {formatCurrency(coverage.unnameable_spend)} of spend has no campaign name in
                <code className="mx-1 px-1 bg-amber-100 rounded">ad_platform.ad_spend</code>
                and cannot be tagged here at any effort — this is all Google spend. Fixing it means
                populating <code className="mx-1 px-1 bg-amber-100 rounded">campaign_name</code> in
                the accounting-lambda sync, which caps coverage at {formatPercent(coverage.max_achievable_share)} until then.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      <div className="flex gap-2">
        {[
          { value: 'untagged', label: 'Needs tagging' },
          { value: 'tagged', label: 'Tagged' },
          { value: 'all', label: 'All' },
        ].map(opt => (
          <Button
            key={opt.value}
            variant={filter === opt.value ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter(opt.value)}
          >
            {opt.label}
            <span className="ml-2 opacity-70">
              {opt.value === 'untagged'
                ? campaigns.filter(c => !c.goal && !c.unnameable).length
                : opt.value === 'tagged'
                  ? campaigns.filter(c => c.goal).length
                  : campaigns.length}
            </span>
          </Button>
        ))}
      </div>

      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="border-b bg-gray-50">
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                <th className="px-4 py-3 font-medium">Campaign</th>
                <th className="px-4 py-3 font-medium">Channel</th>
                <th className="px-4 py-3 font-medium text-right">Lifetime spend</th>
                <th className="px-4 py-3 font-medium">Goal</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                    {filter === 'untagged' ? 'Every nameable campaign is tagged.' : 'No campaigns.'}
                  </td>
                </tr>
              )}
              {filtered.map(c => {
                const key = `${c.channel}|${c.campaign_name}`
                const isSaving = saving === key
                return (
                  <tr key={key} className={c.unnameable ? 'bg-gray-50' : undefined}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">
                        {c.campaign_name || <span className="italic text-gray-500">No campaign name</span>}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        {c.brand && <span className="text-xs text-gray-500">{c.brand}</span>}
                        {c.suggested_goal && (
                          <button
                            type="button"
                            onClick={() => setGoal(c, c.suggested_goal)}
                            disabled={isSaving}
                            className="inline-flex items-center gap-1 text-xs text-blue-700 hover:text-blue-900 hover:underline"
                            title="Suggested from the campaign name — click to accept"
                          >
                            <Sparkles className="h-3 w-3" />
                            suggests {c.suggested_goal}
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      <span className="inline-flex items-center gap-1.5">
                        <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: channelColor(c.channel) }} />
                        {channelLabel(c.channel)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-gray-900">
                      {formatCurrency(c.spend)}
                    </td>
                    <td className="px-4 py-3">
                      {c.unnameable ? (
                        <Badge variant="outline" className="text-gray-500">
                          Not taggable
                        </Badge>
                      ) : (
                        <div className="flex items-center gap-2">
                          <select
                            value={c.goal || ''}
                            disabled={isSaving}
                            onChange={e => setGoal(c, e.target.value)}
                            className={`text-sm border rounded px-2 py-1 ${c.goal ? GOAL_STYLES[c.goal] : 'bg-white text-gray-500 border-gray-300'}`}
                          >
                            <option value="">— not set —</option>
                            {goals.map(g => (
                              <option key={g.value} value={g.value}>{g.label}</option>
                            ))}
                          </select>
                          {isSaving && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
                          {!isSaving && c.goal && <Check className="h-4 w-4 text-emerald-600" />}
                        </div>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  )
}
